import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { EventScope } from '@autopass-web/mochi-analytics/event';
import { MochiButton } from '@autopass-web/mochi-component';
import { InfoCircleFillIcon, MapPinFillIcon } from '@autopass-web/mochi-icons';
import {
  PaymentOptions,
  type PaymentOptionsControlRef,
} from '@autopass-web/mochi-payment-module';
import { axiosAutoPassErrorConverter } from '@autopass-web/mochi-utils';

import { Chyron } from '../../components/chyron';
import {
  DpassSignupCheckbox,
  DpassSignupDialog,
  useDpassEmailPrompt,
} from '../../components/dpass-signup-dialog';
import { MainLayout } from '../../components/main-layout';
import { Processing } from '../../components/processing';
import { AMPLITUDE_KEY, analyticsService } from '../../services/analytics';
import {
  getDpassSignupDefaultValue,
  useCreateDpassMembership,
} from '../../services/dpass-register';
import { useInvoice } from '../../services/use-invoice';
import { useAppContext } from '../../stores/app-context';
import { getDpassSignupRecord } from '../../stores/use-dpass-signup-record';
import { useSubmitRecordsV2 } from '../../stores/use-submit-records-v2';
import { createApplePayTransaction } from '../../utils/create-apple-pay-transaction';
import { createCreditCardTransactionThroughMochiPay } from '../../utils/create-credit-card-transaction-through-mochi-pay';
import { getAutoPartnerPaymentUrl } from '../../utils/get-autopartner-payment-url';
import { getAutopassPaymentUrl } from '../../utils/get-autopass-payment-url';
import { getEasyWalletInitUrl } from '../../utils/get-easy-wallet-init-url';
import { getJKOPayInitUrl } from '../../utils/get-jko-pay-init-url';
import { getLinePayInitUrl } from '../../utils/get-line-pay-init-url';
import { getTaiwanPayInitUrl } from '../../utils/get-taiwan-pay-init-url';
import { useDedupedCallback } from '../../utils/use-deduped-callback';
import { DEFAULT_RECEIPT_SET, getSupportPayments } from './utils';

import type {
  ChainStoreInvoice,
  ChainStoreMerchant,
  SupportReceiptType,
} from '../../types';
import type { creditCardSchema, emailSchema } from './utils';
import type {
  B2bReceiptInfo,
  CarrierMobileReceiptInfo,
  DonateReceiptInfo,
} from '@autopass-web/mochi-payment-module';
import type { PaymentMethodType } from '@autopass-web/types';
import type { z } from 'zod';

type Receipt = {
  type: SupportReceiptType;
  ubn?: string;
  barcode?: string;
  donate?: string;
  name: string;
  phone: string;
};

type PaymentInfo = {
  receipt: Receipt;
  email: z.infer<typeof emailSchema>;
  creditCard: Required<z.infer<typeof creditCardSchema>>;
  paymentMethod: PaymentMethodType;
};

type PaymentConfig = {
  value: PaymentMethodType;
  available: boolean;
};

const DPASS_MEMBERSHIP_SIGNUP_STATUS_EVENT =
  '付款與發票設定頁_加入駕駛中心_帳號送出';

const Payment = ({
  invoice,
  handleSubmitPaymentInfo,
}: {
  invoice: ChainStoreInvoice;
  merchant: ChainStoreMerchant;
  handleSubmitPaymentInfo: (paymentInfo: PaymentInfo) => void;
}) => {
  const paymentOptionsRef = useRef<PaymentOptionsControlRef>();
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodType | null>(null);
  const record = getDpassSignupRecord();

  const dpassSignupDefaultOption = getDpassSignupDefaultValue({
    transactionType: invoice.transaction_type,
    paymentMethod: selectedPaymentMethod,
  });

  const dpassSignupVisible = dpassSignupDefaultOption !== undefined && !record;

  const [agreedToDpassMembership, setAgreedToDpassMembership] = useState(
    dpassSignupDefaultOption,
  );

  useEffect(() => {
    if (!dpassSignupVisible) {
      setAgreedToDpassMembership(false);
    }
  }, [dpassSignupVisible]);

  const { createDpassMembership } = useCreateDpassMembership();
  const {
    isOpen: isDpassDialogOpen,
    isSubmitting: isDpassSubmitting,
    promptEmail,
    completePrompt,
    handleSubmit: handleDpassDialogSubmit,
    handleClose: handleDpassDialogClose,
  } = useDpassEmailPrompt({
    onDismiss: () => setAgreedToDpassMembership(false),
  });

  const handleDpassSignup = async (
    emailFromReceipt: string | undefined,
  ): Promise<string | null> => {
    const from = 'chain_store_pay_' + invoice.transaction_type;
    const analyticsMeta = {
      chainStorePartnerName: invoice.chain_store_partner_name,
      chainStoreName: invoice.chain_store_name,
      transactionTypeI18N: invoice.transaction_type_i18n,
    };

    if (!emailFromReceipt) {
      const dialogEmail = await promptEmail();
      if (!dialogEmail) return null;

      let signupStatus: string;

      try {
        await createDpassMembership({ email: dialogEmail, from });
        signupStatus = 'success';
      } catch {
        signupStatus = 'failed';
      } finally {
        analyticsService.logEvent(DPASS_MEMBERSHIP_SIGNUP_STATUS_EVENT, {
          email: dialogEmail,
          特店廠商: analyticsMeta.chainStorePartnerName,
          連鎖店名稱: analyticsMeta.chainStoreName,
          商店服務類型: analyticsMeta.transactionTypeI18N,
          帳號送到駕駛中心: signupStatus,
        });
      }

      completePrompt();
      return dialogEmail;
    }

    try {
      await createDpassMembership({ email: emailFromReceipt, from });
    } catch (error) {
      console.error('create dpass membership error', error);
    }
    return emailFromReceipt;
  };

  const { records: recordsV2 } = useSubmitRecordsV2(invoice.id);

  const supportPayments = getSupportPayments({
    capture_method: invoice.capture_method,
    mochipay_issue_receipt: invoice.mochipay_issue_receipt,
    amount: invoice.amount,
    payment_options: invoice.payment_options,
  });

  const paymentsConfig: PaymentConfig[] = (() => {
    return supportPayments.map((payment) => ({
      value: payment,
      available: (() => {
        return recordsV2.at(-1)?.paymentMethod !== payment;
      })(),
    }));
  })();

  const { chain_store_name, amount, capture_method } = invoice;

  const actionText = capture_method === 'manual' ? '預授權' : '付款';
  const amountText = `${Math.round(+amount)} 元`;
  const ctaButtonText = `${agreedToDpassMembership ? '同意並' : ''}${actionText} ${amountText}`;

  useEffect(() => {
    analyticsService.paymentPageView();
  }, []);

  const handleSubmit = async () => {
    const {
      payment: [paymentFormInfo],
      receipt: [receiptInfo] = [],
    } = await paymentOptionsRef.current.submit();

    // chain-store-pay currently not enable carrier_citizen_digital receipt. but skip the case for the type safety
    if (receiptInfo?.type === 'carrier_citizen_digital') return;

    let email =
      receiptInfo?.type !== 'payment_carrier' && receiptInfo?.type
        ? receiptInfo?.email
        : undefined;
    const selectedPaymentMethod = paymentFormInfo.paymentMethod;
    const creditCard =
      paymentFormInfo.paymentMethod !== 'credit_card'
        ? undefined
        : paymentFormInfo.paymentInfo;

    if (selectedPaymentMethod == null) return;

    analyticsService.paymentPayClick({
      chainStoreName: invoice.chain_store_name,
      chainStorePartnerName: invoice.chain_store_partner_name,
      captureType: invoice.capture_method,
      email: email,
      paymentMethod: selectedPaymentMethod,
      transactionTypeI18N: invoice.transaction_type_i18n,
      receiptType: receiptInfo?.type,
      checked: agreedToDpassMembership,
    });

    const receipt: Receipt = (() => {
      // Backend still need receipt filed even mochipay_issue_receipt is false, thats weirdo. Give the nonsense value for workaround
      if (!invoice.mochipay_issue_receipt) {
        return {
          love_code: DEFAULT_RECEIPT_SET.WITHOUT_EMAIL.value,
          type: DEFAULT_RECEIPT_SET.WITHOUT_EMAIL.type,
          name: '',
          phone: '',
        };
      }

      if (!receiptInfo) {
        return {
          love_code: DEFAULT_RECEIPT_SET.WITHOUT_EMAIL.value,
          type: DEFAULT_RECEIPT_SET.WITHOUT_EMAIL.type,
          name: '',
          phone: '',
        };
      }

      switch (receiptInfo.type) {
        case 'b2b':
          return {
            name: '',
            phone: '',
            type: 'b2b',
            ubn: (receiptInfo as B2bReceiptInfo).ubn,
          };
        case 'carrier_member':
          return {
            name: '',
            phone: '',
            type: 'carrier_member',
          };
        case 'carrier_mobile_barcode':
          return {
            name: '',
            phone: '',
            barcode: (receiptInfo as CarrierMobileReceiptInfo).barcode,
            type: 'carrier_mobile_barcode',
          };
        case 'donate':
          return {
            name: '',
            phone: '',
            love_code: (receiptInfo as DonateReceiptInfo).love_code,
            type: 'donate',
          };
        case 'payment_carrier':
          return {
            name: '',
            phone: '',
            type: 'payment_carrier',
          };
        default:
          throw new Error('receipt type not found');
      }
    })();

    if (agreedToDpassMembership && dpassSignupVisible) {
      const resolvedEmail = await handleDpassSignup(email);
      if (resolvedEmail === null) return;
      email = resolvedEmail;
    }

    handleSubmitPaymentInfo({
      receipt,
      email,
      creditCard,
      paymentMethod: selectedPaymentMethod,
    });
  };

  return (
    <EventScope.Page page="付款與發票設定頁">
      <MainLayout>
        <div className="flex items-center justify-center w-full bg-[#0075FF]">
          <div className="flex items-center text-white min-h-[50px] gap-0.5">
            <MapPinFillIcon color="white" width={20} height={20} />
            <span className="text-[14px]">{chain_store_name}</span>
          </div>
        </div>
        <Chyron
          shouldShow={(b) => {
            return b.attributes.transaction_type.includes(
              invoice.transaction_type,
            );
          }}
        />
        <div className="flex flex-col items-stretch py-6 px-5 w-full">
          <div className="flex flex-col items-stretch gap-[16px]">
            <PaymentOptions
              availMethods={paymentsConfig}
              controlRef={paymentOptionsRef}
              analKey={AMPLITUDE_KEY}
              isReceiptEnabled={invoice.mochipay_issue_receipt}
              onMethodChange={(method) => {
                setSelectedPaymentMethod(method);
              }}
              methodHint={(method) => {
                if (
                  method === 'jkopay' &&
                  invoice.capture_method === 'manual'
                ) {
                  return (
                    <div className="flex gap-1 px-2 py-3 rounded-xl bg-[#F2F8FF] text-xs">
                      <div>
                        <InfoCircleFillIcon className="h-[15px] w-[15px]" />
                      </div>
                      <div>
                        {`使用此付款方式將會實際請款 ${+invoice.amount} 元，完成服務後將執行差額退款，扣款時請不用擔心。`}
                      </div>
                    </div>
                  );
                }

                return null;
              }}
              eventRegistry={{
                paymentOptionClick: ({
                  method,
                }: {
                  method: PaymentMethodType;
                }) => {
                  analyticsService.selectPaymentMethodClick({
                    chainStorePartnerName: invoice.chain_store_partner_name,
                    chainStoreName: invoice.chain_store_name,
                    transactionTypeI18N: invoice.transaction_type_i18n,
                    captureType: invoice.capture_method,
                    paymentMethod: method,
                  });
                },
                receiptOptionClick: ({
                  method,
                }: {
                  method: SupportReceiptType;
                }) => {
                  analyticsService.selectReceiptTypeClick({
                    chainStorePartnerName: invoice.chain_store_partner_name,
                    chainStoreName: invoice.chain_store_name,
                    transactionTypeI18N: invoice.transaction_type_i18n,
                    captureType: invoice.capture_method,
                    receiptType: method,
                  });
                },
              }}
            />
          </div>
        </div>

        {dpassSignupVisible && (
          <DpassSignupCheckbox
            checked={agreedToDpassMembership}
            onChange={setAgreedToDpassMembership}
          />
        )}

        <div className="w-[80%] mx-auto flex flex-col gap-4 mt-[6px]">
          <MochiButton variant="solid-round" onClick={handleSubmit}>
            {ctaButtonText}
          </MochiButton>
        </div>
      </MainLayout>

      <DpassSignupDialog
        isOpen={isDpassDialogOpen}
        isSubmitting={isDpassSubmitting}
        onClose={handleDpassDialogClose}
        onSubmit={handleDpassDialogSubmit}
      />
    </EventScope.Page>
  );
};

const Transaction = ({
  handlePageRender,
}: {
  handlePageRender?: () => void;
}) => {
  useEffect(() => {
    setTimeout(() => {
      handlePageRender?.();
    }, 500);
  }, [handlePageRender]);

  return <Processing />;
};

class ExpectedError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ExpectedError';
  }
}

const Page = () => {
  const { onToastError } = useAppContext();
  const { invoiceId } = useParams();

  const isInvoiceIdValid = typeof invoiceId === 'string';

  const { invoice, merchant, refetch } = useInvoice(
    { invoiceId },
    {
      enabled: isInvoiceIdValid,
    },
  );

  const [transactionFunc, setTransactionFunc] =
    useState<() => Promise<unknown>>();
  const [isTransacting, setIsTransacting] = useState(false);

  // prevent execute Transaction twice so wrapped with the hook
  const executeTransaction = useDedupedCallback(async () => {
    try {
      if (!transactionFunc) {
        return;
      }
      setIsTransacting(true);
      await transactionFunc();
    } catch (error) {
      console.trace();
      console.log('error', error);

      if (!(error instanceof ExpectedError)) {
        const autopassError = axiosAutoPassErrorConverter(error);
        if (autopassError == null) return;

        const { code, message } = autopassError;
        onToastError(message);

        analyticsService.paymentErrorPageView({
          chainStorePartnerName: invoice.chain_store_partner_name,
          chainStoreName: invoice.chain_store_name,
          transactionTypeI18N: invoice.transaction_type_i18n,
          code,
          message,
          error,
        });
      }

      setIsTransacting(false);
      window.location.reload();
    } finally {
      setTransactionFunc(undefined);
      refetch();
    }
  }, [transactionFunc]);

  const handleSubmitPaymentInfo = ({
    paymentMethod,
    email,
    creditCard,
    receipt,
  }: PaymentInfo) => {
    const callbackParams = {
      serviceUid: invoice.id,
      captureMethod: invoice.capture_method,
      amount: invoice.amount,
      merchantUid: merchant.uid,
    };

    const paymentTransactions = {
      credit_card: () => {
        try {
          return createCreditCardTransactionThroughMochiPay({
            invoice,
            creditCard,
            email,
            receipt,
            merchantUid: merchant.uid,
          });
        } catch {
          console.log('付款失敗，請重新付款');

          return Promise.reject('付款失敗，請重新付款');
        }
      },
      apple_pay: async () => {
        const paymentUrl = await new Promise<string>((resolve, reject) => {
          createApplePayTransaction(
            {
              onSuccess: ({ callbackUrl }) => {
                resolve(callbackUrl);
              },
              onError: (message) => {
                reject(new Error(message));
              },
              onCancel: () => {
                reject(new ExpectedError('apple pay canceled'));
              },
            },
            {
              invoice,
              receipt,
            },
          );
        });

        // if there has no paymentUrl, lead to invoice page
        paymentUrl && (window.location.href = paymentUrl);
      },
      easy_wallet: async () => {
        const paymentUrl = await getEasyWalletInitUrl({
          invoice,
          email,
          receipt,
          callbackParams,
        });

        window.location.href = paymentUrl;
      },
      jkopay: async () => {
        const paymentUrl = await getJKOPayInitUrl({
          invoice,
          email,
          receipt,
          callbackParams,
        });

        window.location.href = paymentUrl;
      },
      line_pay: async () => {
        const paymentUrl = await getLinePayInitUrl({
          invoice,
          receipt,
          callbackParams,
          email,
        });
        analyticsService.redirectPaymentLinkLinePay();
        window.location.href = paymentUrl;
      },
      autopass: async () => {
        const paymentUrl = await getAutopassPaymentUrl({
          invoiceId: invoice.id,
        });
        analyticsService.redirectPaymentLinkAutopass();
        window.location.href = paymentUrl;
      },
      taiwan_pay: async () => {
        const paymentUrl = await getTaiwanPayInitUrl({
          invoice,
          email,
          receipt,
          callbackParams,
        });

        window.location.href = paymentUrl;
      },
      auto_partner: async () => {
        const paymentUrl = await getAutoPartnerPaymentUrl({
          invoiceId: invoice.id,
        });
        window.location.href = paymentUrl;
      },
    } as const satisfies Partial<
      Record<PaymentMethodType, () => Promise<unknown>>
    >;

    const paymentTransaction = paymentTransactions?.[paymentMethod];

    if (paymentTransaction) {
      setTransactionFunc(() => paymentTransaction);
    } else {
      onToastError('使用不可使用的付款方式，請聯繫站方');
      console.error('invalide payment method');
    }
  };

  if (!!transactionFunc || isTransacting) {
    return <Transaction handlePageRender={executeTransaction} />;
  }

  return (
    <Payment
      handleSubmitPaymentInfo={handleSubmitPaymentInfo}
      invoice={invoice}
      merchant={merchant}
    />
  );
};

export { Page as Payment };
